/**
 * Global DOM-reconciliation safety patch.
 *
 * React reconciles by calling the low-level `Node.prototype.removeChild` /
 * `Node.prototype.insertBefore` against the DOM positions it last recorded.
 * When a *third party* mutates the live DOM out of band, those recorded
 * positions go stale and React throws on the next unmount:
 *
 *   NotFoundError: Failed to execute 'removeChild' on 'Node':
 *   The node to be removed is not a child of this node.
 *
 * In this app several actors mutate `document.body`'s direct children behind
 * React's back:
 *   - PostHog session recording (rrweb) injected from `public/index.html`
 *   - the Emergent visual-edit script (also in `index.html`)
 *   - download helpers that do `document.body.appendChild(link)` then remove it
 *   - Radix dialog portals + `sonner` toasts
 *   - browser extensions (Google Translate, etc.) that wrap/replace text nodes
 *
 * Any one of these can leave a node in a different parent than React expects,
 * so a routine route change (which unmounts a large subtree) crashes the whole
 * app. This patch makes the two reconciler primitives defensive: if the node
 * is no longer where it is claimed to be, we no-op gracefully instead of
 * throwing. React then continues its commit and self-heals on the next render.
 *
 * This is the standard, widely-used mitigation for this class of crash. It is
 * applied once, synchronously, before React mounts (see `src/index.js`).
 */
export function installDomSafetyPatch() {
  if (typeof Node !== 'function' || !Node.prototype) return;
  if (Node.prototype.__domSafetyPatched) return;

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function removeChildSafe(child) {
    if (child && child.parentNode !== this) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          '[domSafetyPatch] Skipped removeChild: node is not a child of the ' +
            'expected parent (likely mutated by a third-party script).',
          child,
        );
      }
      return child;
    }
    return originalRemoveChild.apply(this, arguments);
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function insertBeforeSafe(newNode, referenceNode) {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          '[domSafetyPatch] Reference node is not a child of the expected ' +
            'parent; appending instead of throwing.',
          referenceNode,
        );
      }
      return originalInsertBefore.call(this, newNode, null);
    }
    return originalInsertBefore.apply(this, arguments);
  };

  Object.defineProperty(Node.prototype, '__domSafetyPatched', {
    value: true,
    enumerable: false,
  });
}
